<script>
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import Customers from '@/services/Customers';

export default {
  name: 'BusinessNewsAudienceTargeting',
  components: {
    Multiselect,
  },
  props: {
    entityLabel: {
      type: String,
      default: 'business news article',
    },
  },
  data() {
    return {
      recipient: '',
      systemGroups: [],
      userCreatedGroups: [],
      groupsLoading: false,
      selectedSystemGroupId: '',
      selectedUserGroup: null,
      userGroupSearch: '',
      businessLabels: [],
      labelsLoading: false,
      selectedLabels: [],
      audienceOperator: 'OR',
    };
  },
  computed: {
    filteredUserCreatedForSearch() {
      const q = (this.userGroupSearch || '').trim().toLowerCase();
      const list = this.userCreatedGroups;
      if (!q) {
        return list;
      }
      return list.filter((g) => (g.name || '').toLowerCase().includes(q));
    },
    selectedSystemGroup() {
      if (!this.selectedSystemGroupId) {
        return null;
      }
      return this.systemGroups.find((g) => g.group_id === this.selectedSystemGroupId) || null;
    },
    activeAudienceLabel() {
      if (this.selectedSystemGroup) {
        return (this.selectedSystemGroup.name || '').trim();
      }
      if (this.selectedUserGroup) {
        return (this.selectedUserGroup.name || '').trim();
      }
      return '';
    },
    hasActiveAudience() {
      return !!(this.selectedSystemGroupId || this.selectedUserGroup);
    },
    anyAudienceDataLoading() {
      return this.groupsLoading || this.labelsLoading;
    },
    selectedLabelIds() {
      return (this.selectedLabels || [])
        .map((label) => Number(label.id))
        .filter((id) => !Number.isNaN(id) && id > 0);
    },
    showAudienceOperator() {
      return this.recipient === 'LABELS' && this.selectedLabelIds.length > 0;
    },
    normalizedAudienceOperator() {
      return String(this.audienceOperator || 'OR').toUpperCase() === 'AND' ? 'AND' : 'OR';
    },
    hasSelectableGroups() {
      return this.systemGroups.length > 0 || this.userCreatedGroups.length > 0;
    },
  },
  // Groups/labels load on modal open via onShown() — not on component create.
  methods: {
    async onShown() {
      await Promise.all([this.loadGroups(), this.loadBusinessLabels()]);
    },
    async loadGroups() {
      if (this.groupsLoading) {
        return;
      }
      this.groupsLoading = true;
      try {
        const response = await Customers.getCustomersGroups();
        const rows = response.data.data ? response.data.data : [];
        const isLabelBackedGroup = (g) => {
          const v = g && g.label_id;
          if (v === undefined || v === null) return false;
          const s = String(v).trim();
          return s !== '' && s !== '0';
        };

        const activeNonLabelGroups = rows.filter(
          (g) => Number(g.active) === 1 && !isLabelBackedGroup(g)
        );

        this.systemGroups = activeNonLabelGroups.filter((g) => Number(g.system) === 1);
        this.userCreatedGroups = activeNonLabelGroups.filter(
          (g) => Number(g.system) === 0 && g.type === 'static'
        );
        if (!this.hasSelectableGroups && this.recipient === 'GROUP') {
          this.recipient = '';
          this.selectedSystemGroupId = '';
          this.selectedUserGroup = null;
          this.userGroupSearch = '';
        }
      } catch (error) {
        this.systemGroups = [];
        this.userCreatedGroups = [];
        if (this.recipient === 'GROUP') {
          this.recipient = '';
          this.selectedSystemGroupId = '';
          this.selectedUserGroup = null;
          this.userGroupSearch = '';
        }
      } finally {
        this.groupsLoading = false;
      }
    },
    async loadBusinessLabels() {
      if (this.labelsLoading) {
        return;
      }
      this.labelsLoading = true;
      try {
        const response = await Customers.getCustomersLabel({
          is_business_label: 1,
          active: 1,
        });
        this.businessLabels = response.data.data || [];
      } catch (error) {
        this.businessLabels = [];
      } finally {
        this.labelsLoading = false;
      }
    },
    labelBadgeStyle(label) {
      const bg = (label && label.color) || '#6c757d';
      return {
        backgroundColor: bg,
        color: this.labelTextColor(bg),
      };
    },
    labelTextColor(hex) {
      if (!hex) {
        return '#fff';
      }
      const color = String(hex).replace('#', '');
      if (color.length !== 6) {
        return '#fff';
      }
      const r = parseInt(color.substring(0, 2), 16);
      const g = parseInt(color.substring(2, 4), 16);
      const b = parseInt(color.substring(4, 6), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.6 ? '#212529' : '#fff';
    },
    getSelectedGroup() {
      if (this.selectedSystemGroupId) {
        return (
          this.systemGroups.find((g) => g.group_id === this.selectedSystemGroupId)
          || this.userCreatedGroups.find((g) => g.group_id === this.selectedSystemGroupId)
          || null
        );
      }
      return this.selectedUserGroup;
    },
    getSelectedGroupId() {
      const group = this.getSelectedGroup();
      return group && group.group_id ? String(group.group_id) : '';
    },
    validate() {
      if (this.recipient === 'GROUP' && !this.getSelectedGroupId()) {
        return 'Please select an audience group.';
      }
      if (this.recipient === 'LABELS' && !this.selectedLabelIds.length) {
        return 'Please select at least one business label.';
      }
      if (
        this.recipient === 'LABELS'
        && this.selectedLabelIds.length > 1
        && !['OR', 'AND'].includes(this.normalizedAudienceOperator)
      ) {
        return 'Please select an audience operator (OR or AND).';
      }
      return null;
    },
    appendToCreateFormData(formData) {
      if (this.recipient) {
        formData.append('audience_type', this.recipient);
      }

      if (this.recipient === 'GROUP') {
        const groupId = this.getSelectedGroupId();
        if (groupId) {
          formData.append('group_id', groupId);
          formData.append('group_ids[]', groupId);
        }
        formData.append('audience_operator', 'OR');
        return;
      }

      if (this.recipient === 'LABELS' && this.selectedLabelIds.length) {
        formData.append('label_id', this.selectedLabelIds[0]);
        this.selectedLabelIds.forEach((labelId) => {
          formData.append('label_ids[]', labelId);
        });
        formData.append('audience_operator', this.normalizedAudienceOperator);
      }
    },
    appendToEditFormData(formData, { initialGroupIds = [], initialLabelIds = [] } = {}) {
      if (this.recipient) {
        formData.append('audience_type', this.recipient);
      }

      const normalizedInitialGroupIds = (initialGroupIds || []).map((id) => String(id));
      const normalizedInitialLabelIds = (initialLabelIds || []).map((id) => Number(id));
      const selectedIds = this.selectedLabelIds;

      if (this.recipient === 'GROUP') {
        normalizedInitialLabelIds.forEach((labelId) => {
          formData.append('remove_label_ids[]', labelId);
        });

        const groupId = this.getSelectedGroupId();
        normalizedInitialGroupIds
          .filter((id) => id !== groupId)
          .forEach((id) => formData.append('remove_group_ids[]', id));

        if (groupId && !normalizedInitialGroupIds.includes(groupId)) {
          formData.append('group_id', groupId);
          formData.append('group_ids[]', groupId);
        }
        formData.append('audience_operator', 'OR');
        return;
      }

      if (this.recipient === 'LABELS') {
        // Switching from GROUP → LABELS: drop previous plain groups
        if (!normalizedInitialLabelIds.length && normalizedInitialGroupIds.length) {
          normalizedInitialGroupIds.forEach((groupId) => {
            formData.append('remove_group_ids[]', groupId);
          });
        }

        // Only remove labels that are no longer selected (keeps remaining label groups intact)
        normalizedInitialLabelIds
          .filter((id) => !selectedIds.includes(id))
          .forEach((id) => formData.append('remove_label_ids[]', id));

        // Only add newly selected labels (re-adding existing ones would fail validation)
        const newLabelIds = selectedIds.filter((id) => !normalizedInitialLabelIds.includes(id));
        if (newLabelIds.length) {
          formData.append('label_id', newLabelIds[0]);
          newLabelIds.forEach((labelId) => {
            formData.append('label_ids[]', labelId);
          });
        }

        // Keep operator in sync even when label set is unchanged
        formData.append('audience_operator', this.normalizedAudienceOperator);
        return;
      }

      normalizedInitialLabelIds.forEach((labelId) => {
        formData.append('remove_label_ids[]', labelId);
      });
      normalizedInitialGroupIds.forEach((groupId) => {
        formData.append('remove_group_ids[]', groupId);
      });
      formData.append('audience_operator', 'OR');
    },
    reset() {
      this.recipient = '';
      this.selectedSystemGroupId = '';
      this.selectedUserGroup = null;
      this.userGroupSearch = '';
      this.selectedLabels = [];
      this.audienceOperator = 'OR';
    },
    async loadFromInitialAudience({
      groups = [],
      labels = [],
      audience_type = '',
      audience_operator = 'OR',
    } = {}) {
      this.reset();

      const normalizedGroups = (groups || [])
        .map((group) => {
          if (!group || !group.group_id) {
            return null;
          }
          return {
            group_id: String(group.group_id),
            name: group.name || `Group ${group.group_id}`,
            type: group.type || 'static',
            active: group.active,
            total_members: group.total_members,
            label_id: group.label_id ? Number(group.label_id) : null,
          };
        })
        .filter(Boolean);

      const normalizedLabels = (labels || [])
        .map((label) => {
          if (label == null) {
            return null;
          }
          if (typeof label === 'object') {
            return {
              id: Number(label.id),
              name: label.name || `Label ${label.id}`,
              color: label.color,
            };
          }
          return {
            id: Number(label),
            name: `Label ${label}`,
          };
        })
        .filter((label) => label && !Number.isNaN(label.id));

      if (!normalizedGroups.length && !normalizedLabels.length) {
        return;
      }

      if (!this.systemGroups.length && !this.userCreatedGroups.length) {
        await this.loadGroups();
      }

      const labelBackedGroups = normalizedGroups.filter((group) => group.label_id);
      const resolvedAudienceType =
        audience_type === 'LABELS' || labelBackedGroups.length ? 'LABELS' : 'GROUP';

      if (resolvedAudienceType === 'LABELS') {
        if (!this.businessLabels.length) {
          await this.loadBusinessLabels();
        }

        const labelsFromGroups = labelBackedGroups.map((group) => ({
          id: Number(group.label_id),
          name: group.name || `Label ${group.label_id}`,
        }));

        const labelsToSelect = normalizedLabels.length ? normalizedLabels : labelsFromGroups;
        const selected = [];

        labelsToSelect.forEach((labelFromApi) => {
          const existing = this.businessLabels.find(
            (option) => Number(option.id) === Number(labelFromApi.id)
          );
          if (existing) {
            selected.push(existing);
          } else {
            const fallback = { ...labelFromApi };
            this.businessLabels.push(fallback);
            selected.push(fallback);
          }
        });

        this.recipient = 'LABELS';
        this.selectedLabels = selected;
        const operator = String(audience_operator || 'OR').toUpperCase();
        this.audienceOperator = operator === 'AND' ? 'AND' : 'OR';
        return;
      }

      const group = normalizedGroups[0];
      this.recipient = 'GROUP';
      this.audienceOperator = 'OR';

      if (group.type === 'static') {
        const exists = this.userCreatedGroups.some((item) => item.group_id === group.group_id);
        if (!exists) {
          this.userCreatedGroups.push({ ...group });
        }
        this.selectedUserGroup = { ...group };
      } else {
        const exists = this.systemGroups.some((item) => item.group_id === group.group_id);
        if (!exists) {
          this.systemGroups.push({ ...group });
        }
        this.selectedSystemGroupId = group.group_id;
      }
    },
    onRecipientChange() {
      if (this.recipient !== 'GROUP') {
        this.selectedSystemGroupId = '';
        this.selectedUserGroup = null;
        this.userGroupSearch = '';
      }
      if (this.recipient !== 'LABELS') {
        this.selectedLabels = [];
        this.audienceOperator = 'OR';
      }
    },
    onSystemGroupChange() {
      if (this.selectedSystemGroupId) {
        this.selectedUserGroup = null;
        this.userGroupSearch = '';
      }
    },
    pickUserCreatedGroup(group) {
      this.selectedUserGroup = { ...group };
      this.selectedSystemGroupId = '';
      this.userGroupSearch = '';
    },
    clearActiveAudience() {
      this.selectedSystemGroupId = '';
      this.selectedUserGroup = null;
      this.userGroupSearch = '';
    },
  },
};
</script>

<template>
  <div class="card business-news-audience-card rounded-3 mb-3">
    <div class="card-body p-4">
      <h6 class="fw-semibold mb-1 d-flex align-items-center gap-2">
        Audience Targeting
        <b-spinner v-if="anyAudienceDataLoading" small class="text-muted"></b-spinner>
      </h6>
      <p class="text-muted small mb-3">Choose who should see this {{ entityLabel }}</p>

      <div class="mb-3">
        <label class="form-label small fw-medium mb-2">Recipients</label>
        <select
          v-model="recipient"
          class="form-control form-select business-news-audience-field"
          @change="onRecipientChange"
        >
          <option value="">All business members</option>
          <option v-if="hasSelectableGroups" value="GROUP">Group</option>
          <option value="LABELS">Labels</option>
        </select>
        <p v-if="anyAudienceDataLoading" class="text-muted extra-small mt-2 mb-0">
          Loading audience data…
        </p>
      </div>

      <div v-if="recipient === 'GROUP'">
        <label class="form-label small fw-medium mb-2">Audience Group</label>

        <template v-if="systemGroups.length">
          <p class="text-muted text-uppercase small fw-semibold mb-2 audience-sub-label">System groups</p>
          <select
            v-model="selectedSystemGroupId"
            class="form-select form-control business-news-audience-field audience-system-select mb-3"
            :disabled="groupsLoading || !!selectedUserGroup"
            @change="onSystemGroupChange"
          >
            <option value="">
              {{ groupsLoading ? 'Loading groups…' : 'Select a system group' }}
            </option>
            <option
              v-for="group in systemGroups"
              :key="group.group_id"
              :value="group.group_id"
            >
              {{ group.name }}
            </option>
          </select>

          <div class="audience-or-divider" aria-hidden="true">
            <span></span>
            <strong>OR</strong>
            <span></span>
          </div>
        </template>

        <p class="text-muted text-uppercase small fw-semibold mb-2 audience-sub-label">
          User created groups
        </p>
        <div class="audience-user-block">
          <div
            class="audience-user-search-wrap"
            :class="{ 'audience-user-search-wrap--muted': !!selectedSystemGroupId }"
          >
            <div class="position-relative">
              <i
                class="uil uil-search position-absolute text-muted audience-search-icon"
                aria-hidden="true"
              ></i>
              <input
                v-model="userGroupSearch"
                type="text"
                class="form-control rounded-3 business-news-audience-field ps-5 audience-user-search-input"
                placeholder="Search user created groups..."
                autocomplete="off"
                :disabled="groupsLoading || !!selectedSystemGroupId"
              />
              <ul
                v-if="filteredUserCreatedForSearch.length > 0 && userGroupSearch.trim() && !selectedSystemGroupId"
                class="list-group position-absolute w-100 shadow-sm mt-1 audience-suggest-list"
              >
                <li
                  v-for="group in filteredUserCreatedForSearch"
                  :key="group.group_id"
                  class="list-group-item list-group-item-action small py-2 cursor-pointer"
                  role="button"
                  @click="pickUserCreatedGroup(group)"
                >
                  {{ group.name }}
                </li>
              </ul>
            </div>
          </div>

          <div
            v-if="hasActiveAudience && activeAudienceLabel"
            class="audience-active-pill d-flex align-items-center justify-content-between gap-2 mt-3"
          >
            <span class="d-flex align-items-center min-w-0 flex-grow-1">
              <span class="audience-active-dot flex-shrink-0" aria-hidden="true"></span>
              <span class="audience-active-name small fw-semibold text-truncate">{{ activeAudienceLabel }}</span>
            </span>
            <button
              type="button"
              class="audience-active-clear flex-shrink-0"
              aria-label="Clear audience group"
              @click="clearActiveAudience"
            >
              ×
            </button>
          </div>
        </div>
      </div>

      <div v-else-if="recipient === 'LABELS'" class="mb-0">
        <label class="form-label small fw-medium mb-2">Business Labels</label>
        <p class="text-muted extra-small mb-2">
          Select one or more labels. Use the operator below when more than one is selected.
        </p>
        <multiselect
          class="label-multiselect"
          v-model="selectedLabels"
          :options="businessLabels"
          :multiple="true"
          :close-on-select="false"
          :loading="labelsLoading"
          :disabled="labelsLoading"
          label="name"
          track-by="id"
          placeholder="Select labels"
        >
          <template slot="option" slot-scope="{ option }">
            <span
              class="label-option-swatch me-2"
              :style="{ backgroundColor: option.color || '#6c757d' }"
            />
            <span>{{ option.name }}</span>
          </template>
          <template slot="tag" slot-scope="{ option, remove }">
            <span class="multiselect__tag label-multiselect-tag" :style="labelBadgeStyle(option)">
              {{ option.name }}
              <i class="multiselect__tag-icon" @click="remove(option)" aria-hidden="true"></i>
            </span>
          </template>
        </multiselect>

        <div v-if="showAudienceOperator" class="mt-3">
          <label class="form-label small fw-medium mb-2">Audience operator</label>
          <select
            v-model="audienceOperator"
            class="form-select form-control business-news-audience-field"
          >
            <option value="OR">OR — match any selected label</option>
            <option value="AND">AND — match all selected labels</option>
          </select>
          <small class="text-muted d-block mt-1">
            <template v-if="selectedLabelIds.length < 2">
              Operator applies when multiple labels are selected.
            </template>
            <template v-else-if="audienceOperator === 'AND'">
              Users must have all {{ selectedLabelIds.length }} selected labels.
            </template>
            <template v-else>
              Users must have at least one of the {{ selectedLabelIds.length }} selected labels.
            </template>
          </small>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.business-news-audience-card {
  border: 1px solid #e9ecef;
  box-shadow: none !important;
}

.business-news-audience-card .extra-small {
  font-size: 0.75rem;
}

.audience-sub-label {
  letter-spacing: 0.03em;
  font-size: 0.7rem;
}

.audience-search-icon {
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 1;
}

.audience-suggest-list {
  z-index: 20;
  max-height: 220px;
  overflow-y: auto;
  border-radius: 0.375rem;
}

.audience-user-search-wrap--muted .audience-user-search-input {
  opacity: 0.75;
  cursor: not-allowed !important;
}

.audience-or-divider {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 0.75rem;
  margin: 0.25rem 0 1rem;
  color: #6c757d;
}

.audience-or-divider span {
  height: 1px;
  background: #dee2e6;
}

.audience-or-divider strong {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.audience-active-pill {
  padding: 0.65rem 0.85rem;
  border-radius: 10px;
  border: 1px solid #b6d4fe;
  background-color: #e8f2fc;
}

.audience-active-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #0d6efd;
  margin-right: 0.65rem;
}

.audience-active-name {
  color: #212529;
}

.audience-active-clear {
  border: none;
  background: transparent;
  padding: 0 0.15rem;
  margin: 0;
  line-height: 1;
  font-size: 1.35rem;
  font-weight: 400;
  color: #0d6efd;
  cursor: pointer;
  border-radius: 6px;
}

.audience-active-clear:hover {
  color: #0a58ca;
  background-color: rgba(13, 110, 253, 0.1);
}

.cursor-pointer {
  cursor: pointer;
}

.label-option-swatch {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  vertical-align: middle;
}
</style>
